import { keyArray } from "@solid-primitives/keyed"
import { createEffect, onCleanup, type Accessor } from "solid-js"
import { Group, type Scene } from "three"
import type { Nullable } from "vite-node"
import { useNodeNeighborBaseFloorElevationReactiveMap } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import { useEntityByEntityTypeIdReactiveMap } from "../../../../../rx/reactive_map/useEntityByEntityTypeIdReactiveMap"
import type { Engine } from "../../../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../../../core/engine/EntityGraphNode/EntityGraphNode.type"
import type { JungleEntity } from "../../../../core/entity/EntityType/Jungle"
import { getJungleEntsFromMap } from "../../../../core/entity/entityTypeMetaList.generated"
import nullIfEmptyArray from "../../../../ldsh/nullIfEmptyArray"
import disposeRecursive from "../../../fn/disposeRecursive"
import createInitialJungleClusters from "./createInitialJungleClusters"
import createJungleTerrainClusterGroup from "./createJungleTerrainClusterGroup"

// const FADE_DURATION = 1000

// pointing diagonally in the same direction
export default function createJungleTerrainEffect(
  engine: Engine,
  scene: Scene,
): void {
  const group = new Group()
  group.name = 'JunglesGroup'
  scene.add(group)

  const neighborBaseElevationReactiveMap = useNodeNeighborBaseFloorElevationReactiveMap(engine)
  
  const entityByTypeMap = useEntityByEntityTypeIdReactiveMap(engine)

  function getChunks() {
    return createInitialJungleClusters(getJungleEntsFromMap(entityByTypeMap))
  }

  // we'll use the first ID as the chunk ID
  function getChunkId(ents: Array<EntityGraphNode<JungleEntity>>) {
    return ents[0].id
  }

  const mapped = keyArray(getChunks, getChunkId, (
    getList: Accessor<Array<EntityGraphNode<JungleEntity>>>,
    // getIndex
  ) => {
    // console.log('mapped', {index: getIndex(), chunk: getChunkId(getList())}, getList().length)

    let clusterGroup: Nullable<Awaited<ReturnType<typeof createJungleTerrainClusterGroup>>>

    // onMount(() => {
    //   console.log('mounted', {chunk: getChunkId(getList()), len: getList().length})
    // })

    onCleanup(async () => {
      // console.log('cleanup', {chunk: getChunkId(getList()), len: getList().length})
      // fadeOutMaterialAndCleanupObject3D(clusterGroup, FADE_DURATION)
      disposeRecursive(clusterGroup)
    })

    createEffect((): void => {
      const nextList = getList()

      createEffect(() => {
        const prevClusterMesh = clusterGroup

        const nextMeshPromise = (nullIfEmptyArray(nextList) && createJungleTerrainClusterGroup(nextList, neighborBaseElevationReactiveMap))
        if (nextMeshPromise) {
          nextMeshPromise.then((nextMesh) => {
            // console.log('nextMesh', nextMesh)
            if (nextMesh) {
              // nextMesh.position.y = DEFAULT_LAND_TILE_ELEVATION - 0.25
              group.add(nextMesh)
            }
            if (prevClusterMesh) {
              prevClusterMesh.visible = false
              prevClusterMesh.removeFromParent()
              disposeRecursive(prevClusterMesh)
            }
            // fadeOutMaterialAndCleanupObject3D(prevMesh, FADE_DURATION)
            clusterGroup = nextMesh          
          })
        }
      })
    })

    return null
  })
  // we need this effect otherwise nothing calls the mapped function
  createEffect(mapped)
}
